import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Checkbox, Drawer } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { LuCheck, LuCircleHelp, LuX } from 'react-icons/lu';
import { getReview, getReviewAlike, setReviewVerdict } from '@utils/api/videoProjects';
import { withArticle } from './corrections';
import { Chip, Chips } from './CountPanel';
import { keyBelongsToFocus } from './keyTarget';

/**
 * Checking the count track by track (backend: routers/video_review.py). One track at a time, lowest score first
 * (look-alikes score low): three pictures, Yes / Not a … / Not sure (Y / N / U, ← back, → skip). A no removes the
 * track from the count as a correction (tool `review`) and every answer is kept. After two no's, the tracks of this
 * video that look like them are listed, none ticked: the person can check them next or remove the ones they tick
 * (tool `review_alike`). Work in progress: what may be ticked for the person is decided by a test (V19).
 */

const tk = ({ theme: t }) => t.token;
// how many "looks like" rows show before "and N more"
const ALIKE_ROWS = 12;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginSM}px;
	color: ${p => tk(p).colorText};
`;

const Muted = styled.div`
	color: ${p => tk(p).colorTextTertiary};
	font-size: ${p => tk(p).fontSizeSM}px;
`;

const Bar = styled.div`
	height: ${p => tk(p).marginXXS}px;
	background: ${p => tk(p).colorBorderSecondary};
	border-radius: ${p => tk(p).borderRadiusXS}px;
	overflow: hidden;
	& > span {
		display: block;
		height: 100%;
		background: ${p => tk(p).colorPrimary};
	}
`;

const Card = styled.div`
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	border-radius: ${p => tk(p).borderRadiusLG * 2}px;
	padding: ${p => tk(p).paddingSM}px;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
`;

const Crops = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: ${p => tk(p).marginXXS}px;
	& img {
		width: 100%;
		aspect-ratio: 1;
		border-radius: ${p => tk(p).borderRadiusSM}px;
		display: block;
	}
`;

// the three answers: the count panel's own pill chips, a little larger; a no in the error colour
const Answer = styled(Chip)`
	padding: ${p => `${tk(p).paddingXS}px ${tk(p).padding}px`};
	font-size: ${p => tk(p).fontSize}px;
`;

const NoAnswer = styled(Answer)`
	border-color: ${p => tk(p).colorError};
	color: ${p => tk(p).colorError};
	&:hover:not(:disabled) {
		border-color: ${p => tk(p).colorError};
		background: ${p => tk(p).colorErrorBg};
	}
`;

const Count = styled.div`
	font-size: ${p => tk(p).fontSizeHeading3}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	& s {
		color: ${p => tk(p).colorTextTertiary};
		font-weight: normal;
	}
`;

const Points = styled.ul`
	margin: 0;
	padding-left: ${p => tk(p).paddingMD}px;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXXS}px;
	font-size: ${p => tk(p).fontSizeSM}px;
	color: ${p => tk(p).colorTextSecondary};
`;

// a picture with the track's box drawn where it is (backend review.json: boxes, shares of the picture's side)
const Pic = styled.div`
	position: relative;
	& > i {
		position: absolute;
		pointer-events: none;
		border: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorWarning}`};
		border-radius: ${p => tk(p).borderRadiusXS}px;
		box-shadow: ${p => `0 0 0 ${tk(p).lineWidth}px ${tk(p).colorBgMask}`};
	}
`;

const boxAt = b => ({
	left: `${b[0] * 100}%`,
	top: `${b[1] * 100}%`,
	width: `${(b[2] - b[0]) * 100}%`,
	height: `${(b[3] - b[1]) * 100}%`,
});

const Answers = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${p => tk(p).marginXS}px;
	& kbd {
		margin-left: ${p => tk(p).marginXXS}px;
		opacity: 0.6;
		font-size: ${p => tk(p).fontSizeXS}px;
	}
`;

const Head = styled.div`
	font-weight: ${p => tk(p).fontWeightStrong};
`;

const Row = styled.label`
	display: grid;
	grid-template-columns: auto ${p => tk(p).controlHeightLG}px 1fr;
	gap: ${p => tk(p).marginXS}px;
	align-items: center;
	cursor: pointer;
	font-size: ${p => tk(p).fontSizeSM}px;
	& img {
		width: ${p => tk(p).controlHeightLG}px;
		height: ${p => tk(p).controlHeightLG}px;
		border-radius: ${p => tk(p).borderRadiusSM}px;
	}
`;

const clock = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const removedIds = edits =>
	new Set(
		(edits || [])
			.filter(e => e.status === 'ready' && (e.type === 'remove' || e.type === 'negative'))
			.flatMap(e => e.tracks || [])
	);

const ReviewDrawer = ({
	open,
	onClose,
	projectUuid,
	jobUuid,
	subject = 'object',
	count = null,
	fps = 30,
	edits = null,
	onAddEdit,
	onUndoEdit,
	onSeek,
}) => {
	const [retry, setRetry] = useState(false);
	const { data: review, refetch } = useQuery({
		queryKey: ['videoReview', projectUuid, jobUuid, retry],
		queryFn: () => getReview(projectUuid, jobUuid, retry),
		enabled: open && !!projectUuid && !!jobUuid,
		select: res => res?.data || null,
		refetchInterval: q => (q.state.data?.data?.status === 'preparing' ? 3000 : false),
	});
	const [verdicts, setVerdicts] = useState({});
	useEffect(() => {
		if (review?.verdicts) setVerdicts(v => ({ ...review.verdicts, ...v }));
	}, [review?.verdicts]);
	const tracks = useMemo(() => review?.tracks || [], [review?.tracks]);
	const byId = useMemo(() => new Map(tracks.map(t => [t.id, t])), [tracks]);
	const removed = useMemo(() => removedIds(edits), [edits]);
	// tracks to check: not answered and not already removed, lowest score first; "check these next" and back move
	// some ahead, skip moves one to the end
	const [first, setFirst] = useState([]);
	const [later, setLater] = useState([]);
	const [history, setHistory] = useState([]);
	const queue = useMemo(() => {
		const open_ = t => !verdicts[t.id] && !removed.has(t.id);
		const ahead = first.map(id => byId.get(id)).filter(t => t && open_(t));
		const behind = later
			.map(id => byId.get(id))
			.filter(t => t && open_(t) && !first.includes(t.id));
		const moved = new Set([...first, ...later]);
		const rest = tracks.filter(t => open_(t) && !moved.has(t.id));
		return [...ahead, ...rest, ...behind];
	}, [tracks, byId, verdicts, removed, first, later]);
	const current = queue[0] || null;
	useEffect(() => {
		if (open && current) onSeek?.(current.t0);
	}, [open, current, onSeek]);

	const no = useMemo(
		() =>
			Object.keys(verdicts)
				.filter(k => verdicts[k] === 'no')
				.map(Number),
		[verdicts]
	);
	const yes = useMemo(
		() =>
			Object.keys(verdicts)
				.filter(k => verdicts[k] === 'yes')
				.map(Number),
		[verdicts]
	);
	const askAlike = review?.alike_available && no.length >= 2;
	const { data: alike } = useQuery({
		queryKey: ['videoReviewAlike', projectUuid, jobUuid, no, yes],
		queryFn: () => getReviewAlike(projectUuid, jobUuid, { no, yes }),
		enabled: open && !!askAlike,
		select: res => res?.data || null,
	});
	const proposals = useMemo(
		() => (alike?.proposals || []).filter(p => !removed.has(p.id) && !verdicts[p.id]),
		[alike, removed, verdicts]
	);
	const [ticked, setTicked] = useState(() => new Set());

	const answer = useCallback(
		(track, verdict) => {
			if (!track) return;
			const before = verdicts[track.id];
			setVerdicts(v => ({ ...v, [track.id]: verdict }));
			setHistory(h => [...h, track.id]);
			setReviewVerdict(projectUuid, jobUuid, track.id, verdict).catch(() => {});
			if (verdict === 'no' && !removed.has(track.id))
				onAddEdit({
					type: 'negative',
					tracks: [track.id],
					frame: Math.round(track.t0 * fps),
					tool: 'review',
				});
			if (before === 'no' && verdict !== 'no') {
				const edit = (edits || []).find(
					e => e.tool === 'review' && (e.tracks || []).includes(track.id) && !e.pending
				);
				if (edit) onUndoEdit(edit.id);
			}
		},
		[verdicts, removed, edits, projectUuid, jobUuid, fps, onAddEdit, onUndoEdit]
	);
	const back = useCallback(() => {
		const id = history[history.length - 1];
		if (id == null) return;
		setHistory(h => h.slice(0, -1));
		const edit = (edits || []).find(
			e => e.tool === 'review' && (e.tracks || []).includes(id) && !e.pending
		);
		if (edit) onUndoEdit(edit.id);
		setVerdicts(v => {
			const next = { ...v };
			delete next[id];
			return next;
		});
		setReviewVerdict(projectUuid, jobUuid, id, null).catch(() => {});
		setFirst(f => [id, ...f.filter(x => x !== id)]);
		setLater(l => l.filter(x => x !== id));
	}, [history, edits, projectUuid, jobUuid, onUndoEdit]);
	const skip = useCallback(() => {
		if (!current) return;
		setFirst(f => f.filter(x => x !== current.id));
		setLater(l => [...l.filter(x => x !== current.id), current.id]);
	}, [current]);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = e => {
			if (keyBelongsToFocus(e)) return;
			const k = e.key.toLowerCase();
			const act = {
				y: () => answer(current, 'yes'),
				n: () => answer(current, 'no'),
				u: () => answer(current, 'unsure'),
				arrowleft: back,
				arrowright: skip,
			}[k];
			if (!act) return;
			e.preventDefault();
			e.stopPropagation();
			act();
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	}, [open, current, answer, back, skip]);

	const removeTicked = () => {
		[...ticked].forEach(id => {
			const t = byId.get(id);
			if (!t || removed.has(id)) return;
			onAddEdit({
				type: 'negative',
				tracks: [id],
				frame: Math.round(t.t0 * fps),
				tool: 'review_alike',
			});
		});
		setTicked(new Set());
	};

	const checked = Object.keys(verdicts).length;
	const tally = Object.values(verdicts).reduce((t, v) => ({ ...t, [v]: (t[v] || 0) + 1 }), {
		yes: 0,
		no: 0,
		unsure: 0,
	});
	// what this check has taken out of the count so far (its own corrections, still in place)
	const byTool = tool =>
		(edits || []).filter(e => e.tool === tool && e.status === 'ready' && !e.pending).length;
	const removedNow = byTool('review') + byTool('review_alike');
	const alikeNow = byTool('review_alike');
	const status = review?.status;
	const notThe = `Not ${withArticle(subject)}`;
	return (
		<Drawer
			open={open}
			onClose={onClose}
			placement="right"
			width={440}
			mask={false}
			title="Check the count"
			destroyOnClose>
			<Body>
				<Muted>
					{checked} of {tracks.length || '…'} checked
					{count != null ? ` · count now ${count}` : ''} · work in progress
				</Muted>
				{tracks.length > 0 && (
					<Bar aria-hidden>
						<span style={{ width: `${(100 * checked) / tracks.length}%` }} />
					</Bar>
				)}
				{(!review || status === 'preparing') && (
					<Muted>Getting the pictures of every track ready…</Muted>
				)}
				{status === 'failed' && (
					<>
						<Muted>{review?.message || 'The pictures could not be made.'}</Muted>
						<Button
							onClick={() => {
								setRetry(true);
								refetch();
							}}>
							Try again
						</Button>
					</>
				)}
				{status === 'too_long' && <Muted>{review?.message}</Muted>}
				{status === 'ready' && current && (
					<Card>
						<Head>
							#{current.id} · {clock(current.t0)}–{clock(current.t1)}
							<Muted as="span"> · score {current.conf.toFixed(2)}</Muted>
						</Head>
						<Crops>
							{current.crops.map((c, i) => (
								<Pic key={i}>
									<img src={c} alt="" />
									{current.boxes?.[i] && <i style={boxAt(current.boxes[i])} />}
								</Pic>
							))}
						</Crops>
						<div>Is this {withArticle(current.label || subject)}?</div>
						<Chips>
							<Answer type="button" $active onClick={() => answer(current, 'yes')}>
								<LuCheck /> Yes
							</Answer>
							<NoAnswer type="button" onClick={() => answer(current, 'no')}>
								<LuX /> {notThe}
							</NoAnswer>
							<Answer type="button" onClick={() => answer(current, 'unsure')}>
								<LuCircleHelp /> Not sure
							</Answer>
						</Chips>
						<Muted>
							Keys: Y yes · N no · U not sure · ← back · → skip · {queue.length - 1}{' '}
							left after this one
						</Muted>
					</Card>
				)}
				{status === 'ready' && !current && tracks.length > 0 && (
					<Card>
						<Head>All tracks checked</Head>
						<Muted>
							Your answers are kept to improve the model next time; the ones you said
							no to are in Your changes.
						</Muted>
					</Card>
				)}
				{status === 'ready' && no.length >= 2 && !review?.alike_available && (
					<Muted>Finding tracks that look alike is not ready for this video yet.</Muted>
				)}
				{askAlike && proposals.length > 0 && (
					<Card>
						<Head>{proposals.length} more look like the ones you said no to</Head>
						<Muted>Nothing is ticked: tick what is not {withArticle(subject)}.</Muted>
						{proposals.slice(0, ALIKE_ROWS).map(p => {
							const t = byId.get(p.id);
							return (
								<Row key={p.id}>
									<Checkbox
										checked={ticked.has(p.id)}
										onChange={e =>
											setTicked(s => {
												const n = new Set(s);
												if (e.target.checked) n.add(p.id);
												else n.delete(p.id);
												return n;
											})
										}
									/>
									{t?.crops?.[0] ? (
										<Pic>
											<img
												src={t.crops[Math.min(1, t.crops.length - 1)]}
												alt=""
											/>
											{t.boxes?.[Math.min(1, t.crops.length - 1)] && (
												<i
													style={boxAt(
														t.boxes[Math.min(1, t.crops.length - 1)]
													)}
												/>
											)}
										</Pic>
									) : (
										<span />
									)}
									<span>
										#{p.id} · {t ? clock(t.t0) : ''} · alike{' '}
										{p.alike.toFixed(2)}
										<Bar>
											<span
												style={{
													width: `${Math.max(0, (p.alike - 0.5) * 200)}%`,
												}}
											/>
										</Bar>
									</span>
								</Row>
							);
						})}
						{proposals.length > ALIKE_ROWS && (
							<Muted>and {proposals.length - ALIKE_ROWS} more</Muted>
						)}
						<Answers>
							<Button
								type="primary"
								onClick={() => {
									const ids = proposals.map(p => p.id);
									setFirst(ids);
									setLater(l => l.filter(x => !ids.includes(x)));
								}}>
								Check these next
							</Button>
							<Button danger disabled={ticked.size === 0} onClick={removeTicked}>
								Remove the {ticked.size} ticked
							</Button>
						</Answers>
					</Card>
				)}
				{askAlike && alike && proposals.length === 0 && (
					<Muted>Nothing else in this video looks like the ones you said no to.</Muted>
				)}
				{status === 'ready' && (
					<Card>
						<Head>What your answers do</Head>
						{count != null && (
							<Count>
								{removedNow > 0 && <s>{count + removedNow}</s>}
								{removedNow > 0 ? ' → ' : ''}
								{count}
								<Muted as="span"> counted now</Muted>
							</Count>
						)}
						<Points>
							<li>
								{notThe} takes the track out of the count at once
								{tally.no || alikeNow
									? ` (${tally.no} by you${alikeNow ? `, ${alikeNow} as alike` : ''})`
									: ''}
								.
							</li>
							<li>
								Yes and Not sure keep it counted ({tally.yes} yes, {tally.unsure}{' '}
								not sure).
							</li>
							<li>
								Every removal is in Your changes, where Undo puts it back; ← goes
								back one track.
							</li>
							<li>
								Your answers are kept with this video, and we use them to improve
								the model next time. Nothing else is changed.
							</li>
						</Points>
					</Card>
				)}
			</Body>
		</Drawer>
	);
};

// the way in, under the count: a card of its own colour, not one more grey button
const EntryCard = styled.button`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${p => tk(p).marginXXS}px;
	width: 100%;
	padding: ${p => `${tk(p).paddingSM}px ${tk(p).padding}px`};
	cursor: pointer;
	font: inherit;
	text-align: left;
	color: ${p => tk(p).colorText};
	background: ${p => tk(p).colorPrimaryBg};
	border: ${p => `${tk(p).lineWidthBold}px solid ${tk(p).colorPrimaryBorder}`};
	border-radius: ${p => tk(p).borderRadiusLG * 2}px;
	& b {
		color: ${p => tk(p).colorPrimary};
		display: flex;
		align-items: center;
		gap: ${p => tk(p).marginXXS}px;
	}
	& span {
		font-size: ${p => tk(p).fontSizeSM}px;
		color: ${p => tk(p).colorTextSecondary};
	}
	&:hover {
		border-color: ${p => tk(p).colorPrimary};
	}
`;

export const ReviewEntry = ({ tracks, onOpen }) => (
	<EntryCard type="button" data-tour="video-review" onClick={onOpen}>
		<b>
			<LuCheck /> Check the count
		</b>
		<span>
			Go through the {tracks} tracks one by one, look-alikes first · about{' '}
			{Math.max(1, Math.round((tracks * 4) / 60))} min
		</span>
	</EntryCard>
);

export default ReviewDrawer;
